import { useCallback, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ToolContainer, FileDropZone } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import type { Mesh } from "@/lib/mesh3d";
import { detectMeshFormat, guessUnits, loadMeshFile, MESH_ACCEPT, UNIT_FACTORS_TO_METRES } from "./meshLoad";
import MeshView3D, { type MeshSection } from "./MeshView3D";
import { formatLength, isLengthUnit, type LengthUnit } from "./meshMeasure";
import MeshReport from "./MeshReport";

const TOOL_INFO_CONTENT = {
  what: `Previews a 3D model (STL, OBJ, PLY, GLB or 3MF) as a lit, solid shape you can orbit and zoom, with an optional wireframe overlay, a click-two-points distance tool, a cross-section slider, plus vertex and triangle counts, size, and 3D-printing checks (watertight, volume, fits-on-bed). Runs in your browser; the file isn't uploaded.`,
  how: `Upload your 3D file, then drag to rotate, scroll or pinch to zoom, and right-drag to pan. Toggle the wireframe or auto-rotate, and use Reset View to go back. Choose Measure and click two points on the model for the straight-line distance, or Cross-section to slice the model open along X, Y or Z. The size is shown in millimetres and inches; set the model units if the size looks off.`,
  tip: `Check the size before sending a file to a 3D printer. STL files have no units, so a model made in millimetres or inches can be off by a large factor. The "Watertight" check also catches models with holes before a slicer does.`,
};

export default function Mesh3DViewer() {
  const [file, setFile] = useState<File | null>(null);
  const [mesh, setMesh] = useState<Mesh | null>(null);
  const [busy, setBusy] = useState(false);
  const [wire, setWire] = useState(false);
  const [spin, setSpin] = useState(false);
  const [reset, setReset] = useState(0);
  const [shown, setShown] = useState(false); // false = loaded but held back because today's trial uses are used up
  const [units, setUnits] = useState<LengthUnit>("mm");
  const [measure, setMeasure] = useState(false);
  const [dist, setDist] = useState<number | null>(null);
  const [cut, setCut] = useState<MeshSection>({ on: false, axis: 1, t: 0.5 });
  const { logUsageNow } = useLogUsageOnAction();

  const showModel = useCallback(async () => { setShown(await logUsageNow("view")); }, [logUsageNow]);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const fmt = detectMeshFormat(f.name);
    if (!fmt) { toast.error("Unsupported file type. Use STL, OBJ, PLY, GLB or 3MF."); return; }
    setBusy(true);
    try {
      const m = await loadMeshFile(f, fmt);
      if (m.vertices.length === 0 || m.triangles.length === 0) { toast.error("No triangles found in this file"); return; }
      // The model is kept either way. The trial use is taken when the model and its report are shown; if today's
      // trial uses are gone the paywall opens and the file stays loaded behind it (never dropped), ready to show.
      const fm = fmt === "glb" ? "m" : fmt === "obj" || fmt === "ply" ? guessUnits(m) : "mm"; // STL/3MF are millimetres by convention
      setFile(f); setMesh(m); setReset(0); setUnits(fm); setMeasure(false); setDist(null); setCut({ on: false, axis: 1, t: 0.5 });
      setShown(await logUsageNow("view"));
    } catch (e) {
      toast.error(e instanceof Error && e.message ? `Couldn't read this file: ${e.message}` : "Couldn't read this file");
    } finally { setBusy(false); }
  }, [logUsageNow]);

  return (
    <ToolContainer>
      {!mesh || !file ? (
        <FileDropZone gateAtDownload accept={MESH_ACCEPT} onFiles={handleFiles} label={busy ? "Reading model..." : "Drop an STL, OBJ, PLY, GLB or 3MF file"} description="Preview a 3D model, solid and orbitable, right in your browser" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex items-center justify-between gap-2">
            <p className="text-sm font-medium truncate">{file.name}</p>
            <Button size="sm" variant="ghost" onClick={() => { setFile(null); setMesh(null); setShown(false); }}>Change file</Button>
          </div>
          {!shown ? (
            <Card className="p-4 space-y-3" data-testid="mesh-withheld">
              <p className="text-sm font-medium">Your model is loaded and ready.</p>
              <p className="text-sm text-muted-foreground">Today's trial uses are used up, so viewing the model was not done. Your file is still here and nothing was lost. Trial uses start fresh tomorrow, or get a pass and show it right away.</p>
              <Button size="sm" onClick={showModel}>Show model</Button>
            </Card>
          ) : (
            <>
              <MeshView3D mesh={mesh} wireframe={wire} autoRotate={spin} resetSignal={reset} measure={measure} onMeasure={setDist} section={cut} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant={wire ? "default" : "outline"} aria-pressed={wire} onClick={() => setWire(!wire)}>Wireframe</Button>
                <Button size="sm" variant={spin ? "default" : "outline"} aria-pressed={spin} onClick={() => setSpin(!spin)}>Auto-rotate</Button>
                <Button size="sm" variant={measure ? "default" : "outline"} aria-pressed={measure} onClick={() => { setMeasure(!measure); setDist(null); }}>Measure</Button>
                <Button size="sm" variant={cut.on ? "default" : "outline"} aria-pressed={cut.on} onClick={() => setCut({ ...cut, on: !cut.on })}>Cross-section</Button>
                <Button size="sm" variant="outline" onClick={() => setReset((n) => n + 1)}>Reset View</Button>
              </div>
              {measure && (
                <p role="status" data-testid="mesh-measure" className="text-sm">
                  {dist === null ? "Click a point on the model, then a second point. Drag to rotate; a click that does not move picks a point." : `Distance: ${formatLength(dist, units)}`}
                </p>
              )}
              {cut.on && (
                <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="mesh-section">
                  <span className="text-xs text-muted-foreground">Cut along</span>
                  {(["X", "Y", "Z"] as const).map((a, i) => (
                    <Button key={a} size="sm" variant={cut.axis === i ? "default" : "outline"} aria-pressed={cut.axis === i} onClick={() => setCut({ ...cut, axis: i as 0 | 1 | 2 })}>{a}</Button>
                  ))}
                  <input type="range" min={0} max={100} step={1} value={Math.round(cut.t * 100)} aria-label="Cut position" className="w-48" onChange={(e) => setCut({ ...cut, t: Number(e.target.value) / 100 })} />
                  <span className="text-xs text-muted-foreground">{cut.t >= 0.999 ? "No cut" : `${Math.round(cut.t * 100)}% along the axis`}</span>
                </div>
              )}
              <div className="max-w-xs">
                <Label className="mb-1 block text-xs">Model units</Label>
                <Select value={units} onValueChange={(v) => { if (isLengthUnit(v)) { setUnits(v); setDist(null); } }}>
                  <SelectTrigger aria-label="Model units"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(UNIT_FACTORS_TO_METRES).map(([k, u]) => <SelectItem key={k} value={k}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">3D files rarely store units. STL and 3MF are assumed to be millimetres, GLB metres; OBJ and PLY are guessed from the size.</p>
              </div>
              <Card className="p-4"><MeshReport mesh={mesh} unitLabel={units} /></Card>
            </>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
